import isEqual from 'lodash/isEqual';
import { GEOFENCE_EVENT_IDS } from '@/constants/global';
import {
	Event,
	EventPayloadData,
	GeofenceEventData,
	ParsedEvent,
	RawEventData,
} from '@/OptimusRoutes/types/rabbitMessage.types';
import { IsValidJSONString } from '@/utilities/util';

type MapEventMatch = {
	storedEventId: number;
	storedData?: RawEventData | null;
	incomingEventId: number;
	incomingData?: RawEventData | null;
	storedTrackingHistoryId?: string | number | null;
	incomingTrackingHistoryId?: string | number | null;
};

type EventMarkerId = {
	trackingHistoryId: string | number;
	eventId: number;
	data?: RawEventData | null;
};

const isGeofenceEventData = (data: EventPayloadData): data is GeofenceEventData => 'placeId' in data;

export const parseEventData = (data?: RawEventData | null): EventPayloadData | null => {
	if (!data) {
		return null;
	}

	if (typeof data !== 'string') {
		return data;
	}

	if (!IsValidJSONString(data)) {
		return null;
	}

	const parsedData = JSON.parse(data) as unknown;
	if (typeof parsedData !== 'object' || parsedData === null) {
		return null;
	}

	return parsedData as EventPayloadData;
};

export const parseEvents = (events: Event[] = []): ParsedEvent[] =>
	events.map((event) => ({ ...event, data: parseEventData(event.data) }));

export const getGeofencePlaceId = (data?: RawEventData | null): number | null => {
	const parsedData = parseEventData(data);

	if (!parsedData || !isGeofenceEventData(parsedData)) {
		return null;
	}

	return Number(parsedData.placeId);
};

export const areMatchingEvents = ({
	storedEventId,
	storedData,
	incomingEventId,
	incomingData,
	storedTrackingHistoryId,
	incomingTrackingHistoryId,
}: MapEventMatch): boolean => {
	if (storedEventId !== incomingEventId) {
		return false;
	}

	if (storedTrackingHistoryId && incomingTrackingHistoryId) {
		if (Number(storedTrackingHistoryId) !== Number(incomingTrackingHistoryId)) {
			return false;
		}
	}

	if (GEOFENCE_EVENT_IDS.includes(incomingEventId) && GEOFENCE_EVENT_IDS.includes(storedEventId)) {
		const storedPlaceId = getGeofencePlaceId(storedData);
		const incomingPlaceId = getGeofencePlaceId(incomingData);

		if (storedPlaceId && incomingPlaceId) {
			return storedPlaceId === incomingPlaceId;
		}
	}

	return true;
};

export const mergeEventPayloadData = (
	stored?: RawEventData | null,
	incoming?: RawEventData | null
): EventPayloadData | null => {
	const parsedStored = parseEventData(stored);
	const parsedIncoming = parseEventData(incoming);

	if (!parsedIncoming) {
		return parsedStored;
	}

	if (!parsedStored) {
		return parsedIncoming;
	}

	if (Array.isArray(parsedStored) || Array.isArray(parsedIncoming)) {
		return parsedIncoming;
	}

	return { ...parsedStored, ...parsedIncoming };
};

export const hasEventDataChanged = (stored?: RawEventData | null, next?: RawEventData | null): boolean =>
	!isEqual(parseEventData(stored), parseEventData(next));

export const getEventMarkerId = ({ trackingHistoryId, eventId, data }: EventMarkerId): string => {
	const placeId = getGeofencePlaceId(data);
	if (placeId) {
		return `event.${trackingHistoryId}.${eventId}.${placeId}`;
	}

	return `event.${trackingHistoryId}.${eventId}`;
};
